import Web3Utils from 'web3-utils'

import { t } from '@/popup/lib/i18n'
import {
  EMAIL_VALIDATOR,
  ILLEGAL_VALIDATOR,
  LOCALHOST_VALIDATOR,
  LOWER_CASE_VALIDATOR,
  NUMBER_VALIDATOR,
  PASSWORD_VALIDATOR,
  SPECIAL_VALIDATOR,
  UPPER_CASE_VALIDATOR,
  URL_VALIDATOR,
  USERNAME_VALIDATOR,
  WS_VALIDATOR,
} from '@/shared/regexValidator'
import {
  InputValidator,
  RequirementsMap,
} from '@/popup/components/shared/Form/FormTypes'
import { MAX_IDLE_TIMEOUT_IN_MINUTES } from '@/shared/config'

/**
 * Validates if password meet requirements
 * @param input
 * @returns error string or null
 */
export const passwordValidator: InputValidator = input => {
  const {
    passed,
    requirements: {
      upperCase,
      lowerCase,
      oneNumber,
      specialCharacter,
      noSpace,
    } = {},
  } = highEntropyPasswordValidator(input)
  if (!upperCase && !lowerCase) {
    return t('input.validators.password_alpha')
  }

  if (!oneNumber) {
    return t('input.validators.password_numeric')
  }

  if (!specialCharacter) {
    return t('input.validators.password_special')
  }

  if (input.length < 8) {
    return t('input.validators.password_length')
  }

  if (!noSpace) {
    return t('input.validators.password_space')
  }

  if (passed) {
    return
  }

  return t('input.validators.password_alpha')
}

export const emailValidator: InputValidator = input =>
  EMAIL_VALIDATOR.test(input) ? undefined : t('input.validators.email')

export const urlValidator: InputValidator = input =>
  URL_VALIDATOR.test(input) ? undefined : t('input.validators.url')

export const wsValidator: InputValidator = input =>
  WS_VALIDATOR.test(input) ? undefined : t('input.validators.ws')

export const localhostValidator: InputValidator = input =>
  LOCALHOST_VALIDATOR.test(input) ? undefined : t('input.validators.localhost')

export const hexValidator: InputValidator = input => {
  if (!Web3Utils.isHexStrict(input)) {
    return t('address_validator.invalid_hex')
  }
}

export const addressValidator: InputValidator = input => {
  if (!Web3Utils.isAddress(input)) {
    return t('address_validator.invalid_address')
  }
}

/**
 * Validates if username meet requirements
 * @param input
 * @returns error string or null
 */
export const usernameValidator: InputValidator = input => {
  if (USERNAME_VALIDATOR.test(input)) {
    return t('input.validators.invalid_character')
  }
}

/**
 * Validates if string contains at least one non-space character
 * @param input
 * @returns error string or null
 */
export const nonSpaceCharacterValidator: InputValidator = input => {
  if (/^\s+$/.test(input)) {
    return t('input.validators.required')
  }
}

export const numberOrHexValidator: InputValidator = input => {
  if (Number.isNaN(+input) && !Web3Utils.isHexStrict(input)) {
    return t('address_validator.invalid_number_or_hex')
  }
}

/**
 * Validates if number meets requirements for Autolock timer
 * @param input
 * @returns error string or null
 */
export const autolockNumberValidator: InputValidator = input => {
  if (Number.parseInt(input) < 1) {
    return t('input.validators.autolock_minimum', { min: 0 })
  }
  if (Number.parseInt(input) > MAX_IDLE_TIMEOUT_IN_MINUTES) {
    return t('input.validators.autolock_maximum', {
      max: MAX_IDLE_TIMEOUT_IN_MINUTES,
    })
  }
}

/**
 * Simple routine to count the number of times the callback returns true
 * The iteration is per character passing a string with one of the chars of
 * input in it
 *
 * @param input: string
 * @param callback: (character: string, index: number) => boolean
 */
const doCount = (
  input: string,
  callback: (c: string, index: number) => boolean
): number => {
  let count = 0
  for (const [i, c] of [...input].entries()) {
    if (callback(c, i)) {
      count++
    }
  }
  return count
}

/**
 * Validates if password meets entropy requirements
 * @param input
 * @returns error string or null
 */
export const highEntropyPasswordValidator = (
  input: string
): {
  requirements: RequirementsMap
  passed: boolean
} => {
  const illegalChars =
    doCount(input, (c: string) => ILLEGAL_VALIDATOR.test(c)) > 0
  const upperCase =
    doCount(input, (c: string) => UPPER_CASE_VALIDATOR.test(c)) > 0
  const lowerCase =
    doCount(input, (c: string) => LOWER_CASE_VALIDATOR.test(c)) > 0
  const oneNumber = doCount(input, (c: string) => NUMBER_VALIDATOR.test(c)) > 0
  const specialCharacter =
    doCount(input, (c: string) => SPECIAL_VALIDATOR.test(c)) > 0
  const noSpace = PASSWORD_VALIDATOR.test(input)
  const eightCharacters = input.length > 8
  return {
    requirements: {
      upperCase,
      lowerCase,
      oneNumber,
      specialCharacter,
      eightCharacters,
      noSpace,
    },
    passed:
      upperCase &&
      lowerCase &&
      oneNumber &&
      specialCharacter &&
      noSpace &&
      !illegalChars &&
      eightCharacters,
  }
}
